import _typeof from "@babel/runtime/helpers/typeof";
// TODO investigate build failures from import of "@chegg/typescript-passport-utils"
// import { decodePassport } from '@chegg/typescript-passport-utils';
import { ActionSeverity, capturePageAction, TeamInfo } from '@chegg/observability';
import { CheggHeaderKeys, CheggTldRegex } from './constants';
import isCheggTld from './isCheggTld';
/**
 * This function accepts an argument that is a Chegg top-level domain,
 * a tld that starts with '.', a domain, a hostname, a url, or
 * a NextJS context object that contains a Passport header or hostname
 * header.  If the function is unable to extract a Chegg TLD it will
 * return undefined.
 *
 * eg. getCheggTldFrom('https://www.chegg.com.mx') => '.com.mx'
 * @param from
 * @returns
 */

function getCheggTldFrom(from) {
  if (typeof from === 'string' && isCheggTld(from)) {
    return from;
  } else if (typeof from === 'string') {
    return getCheggTldFromString(from);
  } else if (_typeof(from) === 'object') {
    return getCheggTldFromContext(from);
  } else {
    capturePageAction({
      action: 'Unable to get tld: missing "from" arg',
      location: 'getCheggTldFrom.ts',
      severity: ActionSeverity.INFO
    }, {
      teamName: TeamInfo['FT-Nexus'].name
    });
    return;
  }
}

/**
 * This function accepts a string that can be a url, a hostname,
 * a domain, or tld, and will parse the given string for a Chegg tld.
 * If a Chegg tld is not found, the function will return undefined.
 *
 * eg. getCheggTldFromString('https://www.chegg.com.mx') => '.com.mx'
 * @param str
 * @returns
 */
function getCheggTldFromString(str) {
  var needsPrefix = str.startsWith('.');
  var needsProtocol = !str.startsWith('https://');
  var cleanUrl = "".concat(needsProtocol ? 'https://' : '').concat(needsPrefix ? 'www' : '').concat(str);
  try {
    var _parsedUrl$hostname$m;
    var parsedUrl = new URL(cleanUrl);
    var tld = (_parsedUrl$hostname$m = parsedUrl.hostname.match(CheggTldRegex)) === null || _parsedUrl$hostname$m === void 0 ? void 0 : _parsedUrl$hostname$m[2];
    if (isCheggTld(tld)) {
      return tld;
    } else {
      // parsed tld is not a Chegg tld
      capturePageAction({
        action: 'Unable to get tld: non-chegg tld',
        location: 'getCheggTldFrom.ts',
        severity: ActionSeverity.INFO
      }, {
        teamName: TeamInfo['FT-Nexus'].name,
        tld: tld !== null && tld !== void 0 ? tld : ''
      });
      return;
    }
  } catch (error) {
    // unable to parse tld from url
    capturePageAction({
      action: 'Unable to get tld: invalid url',
      location: 'getCheggTldFrom.ts',
      severity: ActionSeverity.INFO
    }, {
      teamName: TeamInfo['FT-Nexus'].name,
      cleanUrl: cleanUrl
    });
    return;
  }
}

/**
 * This function accepts a NextJS context object that contains a Passport
 * header or hostname header.  Preference is given to Passport information,
 * because it is a sanitized source of information.
 *
 * eg. getCheggTldFromContext(nextJsPageContext) => '.com.mx'
 * @param context
 * @returns
 */
function getCheggTldFromContext(context) {
  var _context$req;
  var headers = (_context$req = context.req) === null || _context$req === void 0 ? void 0 : _context$req.headers;
  var hostname = undefined;

  // TODO re-enable Passport parsing after typescript-passport-utils build failures fixed
  // if (headers?.[CheggHeaderKeys.PASSPORT]) {
  //   const passportHeader = headers[CheggHeaderKeys.PASSPORT];
  //   const decryptedPassport = decodePassport(passportHeader);
  //   hostname = decryptedPassport?.getHost();
  // } else if (headers?.[CheggHeaderKeys.HOSTNAME]) {
  if (headers !== null && headers !== void 0 && headers[CheggHeaderKeys.HOSTNAME]) {
    hostname = headers[CheggHeaderKeys.HOSTNAME];
  }
  if (!hostname) {
    var _headers$CheggHeaderK;
    capturePageAction({
      action: 'Unable to get tld: missing header or x-chegg-hostname',
      location: 'getCheggTldFrom.ts',
      severity: ActionSeverity.INFO
    }, {
      teamName: TeamInfo['FT-Nexus'].name,
      xCheggHostname: (_headers$CheggHeaderK = headers === null || headers === void 0 ? void 0 : headers[CheggHeaderKeys.HOSTNAME]) !== null && _headers$CheggHeaderK !== void 0 ? _headers$CheggHeaderK : ''
    });
    return;
  }
  return getCheggTldFromString(hostname);
}
export default getCheggTldFrom;